import { Link, useLocation, useParams } from 'react-router-dom'

import { Project } from 'api/types'
import { Icon } from 'components/Icon'
import { tx } from 'lib/i18n'
import { AppLocale } from 'lib/region'

import { useActiveProject } from '../context/ActiveProjectContext'
import { useRegion } from '../context/RegionContext'

type Crumb = { label: string; to?: string }

const globalLabels = (locale: AppLocale): Record<string, string> => ({
  '/admin/invitations': tx(locale, 'الدعوات', 'Invitations'),
  '/admin/roles': tx(locale, 'الأدوار والصلاحيات', 'Roles and permissions'),
  '/admin/settings/appearance': tx(locale, 'الإعدادات · المظهر', 'Settings · Appearance'),
  '/admin/settings/general': tx(locale, 'الإعدادات · عام', 'Settings · General'),
  '/admin/settings/language': tx(locale, 'الإعدادات · اللغة والمنطقة', 'Settings · Language and region'),
  '/admin/settings/notifications': tx(locale, 'الإعدادات · الإشعارات والتذكيرات', 'Settings · Notifications'),
  '/admin/settings/security': tx(locale, 'الإعدادات · الأمان', 'Settings · Security'),
  '/admin/users': tx(locale, 'المستخدمون والأدوار', 'Users and roles'),
  '/analytics': tx(locale, 'التحليلات', 'Analytics'),
  '/approvals': tx(locale, 'الاعتمادات', 'Approvals'),
  '/crew': tx(locale, 'الكوادر', 'Crew'),
  '/dashboard': tx(locale, 'لوحة التحكم', 'Dashboard'),
  '/equipment': tx(locale, 'المعدات', 'Equipment'),
  '/exports': tx(locale, 'التصدير', 'Exports'),
  '/materials': tx(locale, 'المواد', 'Materials'),
  '/profile': tx(locale, 'الملف الشخصي', 'Profile'),
  '/sites': tx(locale, 'المواقع', 'Sites'),
})

const projectTabLabels = (locale: AppLocale): [suffix: string, label: string][] => [
  ['/attachments', tx(locale, 'أرشيف المرفقات', 'Attachments')],
  ['/issues', tx(locale, 'المشاكل', 'Issues')],
  ['/documents', tx(locale, 'المستندات', 'Documents')],
  ['/summaries', tx(locale, 'الملخصات', 'Summaries')],
  ['/settings', tx(locale, 'الإعدادات', 'Settings')],
  ['/overview', tx(locale, 'نظرة عامة', 'Overview')],
  ['/work-areas', tx(locale, 'مناطق العمل', 'Work areas')],
  ['/today', tx(locale, 'اليوم', 'Today')],
  ['/equipment', tx(locale, 'المعدات', 'Equipment')],
  ['/materials', tx(locale, 'المواد', 'Materials')],
  ['/crew', tx(locale, 'الكوادر', 'Crew')],
]

const breadcrumbFor = (pathname: string, projectId: string | undefined, project: Project | null, locale: AppLocale): Crumb[] => {
  const labels = globalLabels(locale)
  if (pathname === '/') return [{ label: tx(locale, 'المشاريع', 'Projects') }]
  if (pathname === '/projects/new') return [{ label: tx(locale, 'المشاريع', 'Projects'), to: '/' }, { label: tx(locale, 'مشروع جديد', 'New project') }]
  if (labels[pathname]) return [{ label: labels[pathname] }]

  if (projectId) {
    const base = `/projects/${projectId}`
    const crumbs: Crumb[] = [
      { label: tx(locale, 'المشاريع', 'Projects'), to: '/' },
      { label: project?.name ?? '…', to: base },
    ]
    const tab = projectTabLabels(locale).find(([suffix]) => pathname.endsWith(suffix))
    if (tab) crumbs.push({ label: tab[1] })
    else if (pathname.includes('/reports/')) crumbs.push({ label: tx(locale, 'التقرير', 'Report') })
    else if (pathname !== base) crumbs.push({ label: tx(locale, 'فهرس التقارير', 'Report index') })
    else crumbs[crumbs.length - 1] = { label: project?.name ?? '…' }
    return crumbs
  }

  return [{ label: tx(locale, 'الصفحة غير موجودة', 'Page not found') }]
}

export const Breadcrumb = () => {
  const location = useLocation()
  const { projectId } = useParams()
  const activeProject = useActiveProject()
  const { locale } = useRegion()
  const crumbs = breadcrumbFor(location.pathname, projectId, activeProject, locale)

  return (
    <nav className="crumbs" aria-label={tx(locale, 'مسار التنقل', 'Breadcrumb')}>
      {crumbs.map((crumb, index) => (
        <span key={`${crumb.label}-${index}`} className="crumbs__entry">
          {index > 0 ? <Icon name={locale === 'en' ? 'chevronRight' : 'chevronLeft'} size={14} /> : null}
          {crumb.to ? (
            <Link to={crumb.to}>{crumb.label}</Link>
          ) : (
            <span aria-current={index === crumbs.length - 1 ? 'page' : undefined}>{crumb.label}</span>
          )}
        </span>
      ))}
    </nav>
  )
}
